/**
 * Browser storage for the Self-Serve enrolment session (ELS-641).
 *
 * - The enrolment JWT is kept in `sessionStorage`, one per tab and per class slug, so it is
 *   gone when the tab closes.
 * - `localStorage` holds only the current "owner" of the class in this browser
 *   (`stdId` + `classId`, never the token, name, or email). When another student redeems
 *   the same class in a different tab, the owner changes and the other tabs are signed out
 *   (see {@link onEnrolmentDisplaced}).
 */
const KEY_PREFIX = "ss-enrolment-token:";
const OWNER_PREFIX = "ss-enrolment-owner:";

/** What a tab needs to call `/enrolled`: the token and the ids it was issued for. */
export interface SsEnrolmentSession {
  enrolmentToken: string;
  stdId: number;
  classId: number;
}

/** The student who most recently redeemed this class in this browser. */
interface SsEnrolmentOwner {
  stdId: number;
  classId: number;
}

/** `sessionStorage` key holding the enrolment session for a class slug. */
function storageKey(slug: string): string {
  return `${KEY_PREFIX}${slug}`;
}

/** `localStorage` key holding the current owner for a class slug. */
function ownerKey(slug: string): string {
  return `${OWNER_PREFIX}${slug}`;
}

/** Route of the Access Simulation page for a class slug. */
export function classAccessSimulationPath(slug: string): string {
  return `/class/${encodeURIComponent(slug)}/access-simulation`;
}

/** Route of the public Self-Serve simulation detail page (ELS-671). */
export function classSimulationPath(slug: string, simId: number): string {
  return `/class/${encodeURIComponent(slug)}/simulation/${simId}`;
}

/**
 * True when the token has no `exp` claim or it is in the past. Client-side convenience
 * only; the backend still validates the token on every request.
 */
function isEnrolmentTokenExpired(token: string | null): boolean {
  const claims = readEnrolmentClaims(token);
  if (claims.exp == null) {
    return true;
  }
  return claims.exp * 1000 <= Date.now();
}

/**
 * Decodes (does not verify) the JWT payload to read `stdId`, `classId`, and `exp`.
 * Any malformed token yields all-null claims.
 */
function readEnrolmentClaims(token: string | null): {
  stdId: number | null;
  classId: number | null;
  exp: number | null;
} {
  if (!token) {
    return { stdId: null, classId: null, exp: null };
  }
  try {
    const segment = token.split(".")[1];
    if (!segment) {
      return { stdId: null, classId: null, exp: null };
    }
    const normalized = segment.replace(/-/g, "+").replace(/_/g, "/");
    const pad = (4 - (normalized.length % 4)) % 4;
    const payload = JSON.parse(atob(`${normalized}${"=".repeat(pad)}`));
    const stdId = Number(payload?.stdId);
    const classId = Number(payload?.classId);
    const exp = typeof payload?.exp === "number" ? payload.exp : null;
    return {
      stdId: Number.isInteger(stdId) ? stdId : null,
      classId: Number.isInteger(classId) ? classId : null,
      exp,
    };
  } catch {
    return { stdId: null, classId: null, exp: null };
  }
}

/** Parses a stored session; anything that isn't the expected JSON shape is ignored. */
function parseStoredSession(raw: string | null): SsEnrolmentSession | null {
  if (!raw) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw);
    if (
      parsed &&
      typeof parsed.enrolmentToken === "string" &&
      Number.isInteger(parsed.stdId) &&
      Number.isInteger(parsed.classId)
    ) {
      return {
        enrolmentToken: parsed.enrolmentToken,
        stdId: parsed.stdId,
        classId: parsed.classId,
      };
    }
  } catch {
    // Legacy raw JWT strings are treated as invalid.
  }
  return null;
}

/** Parses a stored owner; anything that isn't the expected JSON shape is ignored. */
function parseOwner(raw: string | null): SsEnrolmentOwner | null {
  if (!raw) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw);
    if (parsed && Number.isInteger(parsed.stdId) && Number.isInteger(parsed.classId)) {
      return { stdId: parsed.stdId, classId: parsed.classId };
    }
  } catch {
    // ignore
  }
  return null;
}

/** Reads the current owner of a class in this browser, or `null` when unset or unreadable. */
function readOwner(slug: string): SsEnrolmentOwner | null {
  if (!slug || typeof localStorage === "undefined") {
    return null;
  }
  try {
    return parseOwner(localStorage.getItem(ownerKey(slug)));
  } catch {
    return null;
  }
}

/**
 * Records `owner` as the current student for this class. Writing this key fires a
 * `storage` event in the browser's other tabs.
 */
function writeOwner(slug: string, owner: SsEnrolmentOwner): void {
  if (!slug || typeof localStorage === "undefined") {
    return;
  }
  try {
    localStorage.setItem(
      ownerKey(slug),
      JSON.stringify({ stdId: owner.stdId, classId: owner.classId })
    );
  } catch {
    // Ignore quota / private-mode failures.
  }
}

/** True when there is no owner yet, or the session belongs to the current owner. */
function sessionMatchesOwner(
  session: SsEnrolmentSession,
  owner: SsEnrolmentOwner | null
): boolean {
  if (!owner) {
    return true;
  }
  return owner.stdId === session.stdId && owner.classId === session.classId;
}

/**
 * Returns this tab's session for the class when it is still usable, otherwise `null`.
 * An expired token, or one belonging to a student who is no longer the owner, is removed.
 * When no owner is recorded yet, this session becomes the owner.
 *
 * @param slug landing slug
 */
export function getValidEnrolmentSession(slug: string): SsEnrolmentSession | null {
  if (!slug || typeof sessionStorage === "undefined") {
    return null;
  }
  try {
    const session = parseStoredSession(sessionStorage.getItem(storageKey(slug)));
    if (!session || isEnrolmentTokenExpired(session.enrolmentToken)) {
      if (session) {
        clearEnrolmentToken(slug);
      }
      return null;
    }
    const owner = readOwner(slug);
    if (!sessionMatchesOwner(session, owner)) {
      clearEnrolmentToken(slug);
      return null;
    }
    if (!owner) {
      writeOwner(slug, { stdId: session.stdId, classId: session.classId });
    }
    return session;
  } catch {
    return null;
  }
}

/**
 * Stores a new session for this tab after a successful redeem.
 *
 * @param slug landing slug
 * @param session token and ids from the redeem response
 * @param options.claimBrowser default `true`: make this student the owner, signing out other
 *   tabs that hold a different student. Pass `false` to store the session only when it
 *   already belongs to the current owner.
 */
export function setEnrolmentSession(
  slug: string,
  session: SsEnrolmentSession,
  options?: { claimBrowser?: boolean }
): void {
  if (
    !slug ||
    !session?.enrolmentToken ||
    !Number.isInteger(session.stdId) ||
    !Number.isInteger(session.classId) ||
    typeof sessionStorage === "undefined"
  ) {
    return;
  }
  const claimBrowser = options?.claimBrowser !== false;
  if (!claimBrowser && !sessionMatchesOwner(session, readOwner(slug))) {
    return;
  }
  try {
    sessionStorage.setItem(
      storageKey(slug),
      JSON.stringify({
        enrolmentToken: session.enrolmentToken,
        stdId: session.stdId,
        classId: session.classId,
      })
    );
    if (claimBrowser || sessionMatchesOwner(session, readOwner(slug))) {
      writeOwner(slug, { stdId: session.stdId, classId: session.classId });
    }
  } catch {
    // Ignore quota / private-mode failures; user can re-enter the code.
  }
}

/** Removes this tab's session for the class. The owner record is left in place. */
export function clearEnrolmentToken(slug: string): void {
  if (!slug || typeof sessionStorage === "undefined") {
    return;
  }
  try {
    sessionStorage.removeItem(storageKey(slug));
  } catch {
    // ignore
  }
}

/**
 * Fires in other tabs when a different student claims this class in the same browser.
 * Does not put the JWT in localStorage — only stdId/classId for displacement.
 */
export function onEnrolmentDisplaced(
  slug: string,
  onDisplaced: () => void
): () => void {
  if (!slug || typeof window === "undefined") {
    return () => undefined;
  }
  const key = ownerKey(slug);
  const handler = (event: StorageEvent) => {
    if (event.key !== key || event.storageArea !== localStorage) {
      return;
    }
    let session: SsEnrolmentSession | null = null;
    try {
      session = parseStoredSession(sessionStorage.getItem(storageKey(slug)));
    } catch {
      return;
    }
    if (!session) {
      return;
    }
    const owner = parseOwner(event.newValue);
    if (sessionMatchesOwner(session, owner)) {
      return;
    }
    clearEnrolmentToken(slug);
    onDisplaced();
  };
  window.addEventListener("storage", handler);
  return () => window.removeEventListener("storage", handler);
}
